import { Button } from "components/DesignSystem/Button/Button";
import {
  Combobox,
  ComboboxProps,
} from "components/DesignSystem/Combobox/Combobox";
import { LoadingIcon } from "components/icons/LoadingIcon";
import { AddVendorModal } from "components/Vendors/AddVendor/AddVendorModal";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { usePaginatedQuery } from "hooks/usePaginatedQuery";
import { useState } from "react";
import { useLazyGetAllTransactionMerchantsQuery } from "store/apis/merchant";
import { Merchant } from "types/Models/merchant";
import { useDebounce } from "utils/debounce";

export const MerchantSelector = ({
  onMerchantSelect,
  showAction = true,
  ...props
}: ComboboxProps & {
  onMerchantSelect?: (merchant: Merchant) => void;
  showAction?: boolean;
}) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const [search, setSearch] = useState("");
  const addManualVendorModal = useModal();
  const searchTerm = useDebounce(search);

  const { data, loadNext, isLoading, isFetching, reset } = usePaginatedQuery<{
    merchants?: Merchant[];
  }>(
    useLazyGetAllTransactionMerchantsQuery,
    "merchants",
    {
      search_term: searchTerm,
      groupId,
      entityId,
    },
    ["groupId", "entityId"]
  );

  const onSearch = (value: string) => setSearch(value);

  const handleModalClose = () => {
    addManualVendorModal.close();
    reset();
  };

  return (
    <>
      <Combobox
        isDisabled={isLoading || props.isDisabled}
        isLoading={isLoading || props.isLoading}
        options={
          data?.merchants?.map((merchant) => ({
            value: merchant.uuid,
            label: merchant.name,
          })) || []
        }
        onChange={(value) => {
          if (value instanceof Array) return;
          const selectedMerchant = data?.merchants?.find(
            (merchant) => merchant.uuid === value?.value
          );
          if (selectedMerchant && onMerchantSelect) {
            onMerchantSelect(selectedMerchant);
          }
        }}
        onMenuScrollToBottom={loadNext}
        onInputChange={onSearch}
        creatable={false}
        actions={
          isFetching ? (
            <div className="t-flex t-justify-center t-items-center t-w-full t-gap-2 t-p-1.5">
              <span className="t-flex t-origin-center t-animate-spin">
                <LoadingIcon />
              </span>
              Loading...
            </div>
          ) : (
            showAction && (
              <Button
                type="button"
                customType="link"
                block
                onClick={addManualVendorModal.open}
                disabled={isLoading}
              >
                Add Vendor
              </Button>
            )
          )
        }
        {...props}
      />
      <AddVendorModal
        isOpen={addManualVendorModal.isOpen}
        close={handleModalClose}
        entityId={entityId}
      />
    </>
  );
};

export default MerchantSelector;
